import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import {
    DotLegalBreadCrumbModel,
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalOverflowMenuItem,
    DotLegalPageHeader,
    DotLegalTable,
} from "@dotlegal/dotlegal-ui-components";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import { useTranslation } from "@localization/useTranslation";
import FilterBar from "@common/components/filterBar/FilterBar";
import Rocket from "../../processingActivity/processingActivities/rocket.svg?react";
import RiskObservationDialog from "./riskObservationDialog/RiskObservationDialog";
import { DeleteRiskObservationDialog } from "./deleteRiskObservationDialog/DeleteRiskObservationDialog";
import ConvertObservationDialog from "./convertObservationDialog/ConvertObservationDialog";
import ArchiveRiskObservationDialog from "./archiveRiskObservationDialog/ArchiveRiskObservationDialog";
import RiskObservationPublicLinkDialog from "./publicLinkDialog/RiskObservationPublicLinkDialog";
import PurpleLockSvg from "@common/svgs/purpleLock.svg?react";
import RiskObservationStatusChip from "./RiskObservationStatusChip";
import { getObservationHeaders, toSaveModel, useRiskObservationsDataMapping } from "./RiskObservations.hooks";
import { RiskObservationRow, RiskObservationStatus, RiskObservationViewModel } from "./RiskObservations.types";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function RiskObservations() {
    const { translateString, translateDate, translateRiskObservationSource } = useTranslation();
    const {
        isLoading,
        data,
        hasObservations,
        getFilterDataOptions,
        mapSearchFilter,
        showObservationDialog,
        setShowObservationDialog,
        observationForDeletion,
        setObservationForDeletion,
        observationForConvert,
        setObservationForConvert,
        observationForArchive,
        setObservationForArchive,
        selectedObservation,
        setSelectedObservation,
        onSave,
        reactivateObservation,
        searchText,
        setSearchText,
        sort,
        onTableChange,
        onExportClick,
        canCreate,
        canEdit,
        canDelete,
        canConvertToRiskScenario,
        canConvertToIncident,
        isIncidentLimitReached,
        incidentLimitReachedText,
        convertToIncident,
        showPublicLinkDialog,
        setShowPublicLinkDialog,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useRiskObservationsDataMapping();
    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [{ name: translateString("observations") }];

    const openEditDialog = (observation: RiskObservationViewModel) => {
        setSelectedObservation(toSaveModel(observation));
        setShowObservationDialog(true);
    };

    const openCreateDialog = () => {
        setSelectedObservation(undefined);
        setShowObservationDialog(true);
    };

    const getMenuItems = (observation: RiskObservationViewModel) => {
        if (!canEdit) {
            return [];
        }

        const menuItems: Array<DotLegalOverflowMenuItem> = [{ menuItem: translateString("edit"), onClick: () => openEditDialog(observation) }];

        if (observation.canBeConvertedToRiskScenario && canConvertToRiskScenario) {
            menuItems.push({ menuItem: translateString("convertToRiskScenario"), onClick: () => setObservationForConvert(observation) });
        }

        if (observation.canBeConvertedToIncident && canConvertToIncident) {
            menuItems.push(getConvertToIncidentMenuItem(observation));
        }

        if (observation.status === RiskObservationStatus.New) {
            menuItems.push({ menuItem: translateString("archive"), onClick: () => setObservationForArchive(observation) });
        }

        if (observation.status === RiskObservationStatus.Archived) {
            menuItems.push({ menuItem: translateString("reactivateObservation"), onClick: () => reactivateObservation(observation) });

            if (canDelete) {
                menuItems.push({ menuItem: translateString("delete"), onClick: () => setObservationForDeletion(observation) });
            }
        }

        return menuItems;
    };

    const getConvertToIncidentMenuItem = (observation: RiskObservationViewModel): DotLegalOverflowMenuItem => {
        if (isIncidentLimitReached) {
            return {
                menuItem: (
                    <Box sx={{ display: "flex" }}>
                        <span>{translateString("convertToIncident")}</span>
                        <Box sx={{ marginLeft: 0.5 }}>
                            <PurpleLockSvg />
                        </Box>
                    </Box>
                ),
                tooltip: incidentLimitReachedText,
                disabled: true,
                tooltipPosition: "left",
            };
        }

        return { menuItem: translateString("convertToIncident"), onClick: () => convertToIncident(observation) };
    };

    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    canCreate ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {canEdit && (
                                <DotLegalButton buttonType={"secondary"} size="medium" onClick={() => setShowPublicLinkDialog(true)}>
                                    {translateString("reportObservationLink")}
                                </DotLegalButton>
                            )}
                            <DotLegalButton buttonType={"primary"} size="medium" onClick={openCreateDialog}>
                                {translateString("createObservation")}
                            </DotLegalButton>
                        </Box>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {hasObservations ? (
                    <>
                        <ListPageToolbar
                            leftContent={
                                <FilterBar
                                    isLoading={isLoading}
                                    filterData={getFilterDataOptions()}
                                    fetchFilterData
                                    onMenuClose={(options) => mapSearchFilter(options)}
                                    onRemove={(options) => mapSearchFilter(options)}
                                    pinnedFilterIds={pinnedFilterIds}
                                    onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                                    searchValue={searchText}
                                    searchLabel={translateString("search")}
                                    onSearchChange={setSearchText}
                                />
                            }
                            rightContent={<ExcelExportButton onClick={onExportClick} isDisabled={isLoading} />}
                        />
                        <DotLegalTable
                            internalScroll
                            headers={getObservationHeaders(translateString)}
                            rowsPerPage={30}
                            hasOverflowMenu
                            defaultOrderBy={sort.orderBy as keyof RiskObservationRow}
                            defaultOrder={sort.order}
                            onChange={onTableChange}
                            searchString={searchText}
                            isLoading={isLoading}
                            clickableRows={canEdit}
                            paginationLabelOf={translateString("labelOf")}
                            emptyText={translateString("noObservationsMatchesQuery")}
                            data={data}
                            renderRow={(row: RiskObservationRow) => (
                                <TableRow onClick={canEdit ? () => openEditDialog(row) : undefined} key={row.id}>
                                    <TableCell>
                                        <Box sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold })}>
                                            {row.name}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>{translateRiskObservationSource(row.source)}</TableCell>
                                    <TableCell>
                                        <RiskObservationStatusChip status={row.status} />
                                    </TableCell>
                                    <TableCell>{row.observedByWithEmail}</TableCell>
                                    <TableCell>{translateDate(new Date(row.observedDate))}</TableCell>
                                    <TableCell align={"right"}>
                                        <DotLegalOverflowMenu items={getMenuItems(row)} />
                                    </TableCell>
                                </TableRow>
                            )}
                        />
                    </>
                ) : (
                    <DotLegalEmptyState icon={<Rocket />} text={translateString("observationsEmptyState")} />
                )}
            </TablePagePaper>

            {observationForDeletion && (
                <DeleteRiskObservationDialog
                    observationId={observationForDeletion.id}
                    observationName={observationForDeletion.name}
                    onDeletion={onSave}
                    onCloseDialog={() => setObservationForDeletion(undefined)}
                />
            )}

            {observationForConvert && (
                <ConvertObservationDialog observation={observationForConvert} onConverted={onSave} onCloseDialog={() => setObservationForConvert(undefined)} />
            )}

            {showPublicLinkDialog && <RiskObservationPublicLinkDialog onDialogClose={() => setShowPublicLinkDialog(false)} />}

            {observationForArchive && (
                <ArchiveRiskObservationDialog
                    observationId={observationForArchive.id}
                    observationName={observationForArchive.name}
                    onArchived={onSave}
                    onDialogClose={() => setObservationForArchive(undefined)}
                />
            )}

            {showObservationDialog && (
                <RiskObservationDialog onCloseDialog={() => setShowObservationDialog(false)} onSave={onSave} selectedObservation={selectedObservation} />
            )}
        </Box>
    );
}

export default RiskObservations;
